import {
  isTargetingGroup,
  ITargeting,
  ITargetingFilter,
  ITargetingGroup,
  ITargetingGroupData,
  TargetingFilterOperation,
  TargetingOperation,
  targetResults,
} from '@gsc/targeting'
import { TargetingFilterType, TargetingGroupId, WidgetLayout, WidgetSettings } from '@gsc/types'
import { flatten, intersection, uniq } from '../../shared/collection'
import { getCookie } from '../../shared/cookies'
import Emitter from '../../shared/emitter'
import { ActionEventTypes, EventType } from '../tracking'
import { cssSelect } from './cssselector'
import Watchers from './watchers'
import { Storages } from '../storage'
import { ITargetingEnv } from './index'
import { WidgetRuntime } from '../runtime'
import { inlineWidgetSelector } from '../../shared/widget'

function getAllTargetingKeys(widgets: WidgetSettings[]): TargetingFilterType[] {
  const types = flatten(
    widgets.map(({ targeting, layout }) => {
      const explicit = targeting?.group ? flatten(getKeysFromTargetingGroup(targeting.group.items)) : []
      const implicit = layout === WidgetLayout.Inline ? [TargetingFilterType.CssSelector] : []
      return [...explicit, ...implicit]
    }),
  ).filter(Boolean)

  return uniq(types)
}

function getKeysFromTargetingGroup(items: Array<ITargetingGroup | ITargetingFilter>): TargetingFilterType[] {
  return flatten(
    items.map(item =>
      isTargetingGroup(item) ? getKeysFromTargetingGroup(item.items) : (item.type as TargetingFilterType),
    ),
  )
}

function getTargetingGroupOrDefault(widget: WidgetSettings, groupId: string): ITargetingGroup {
  const items = widget.targeting?.group?.items || []
  return (
    (items.find((g: ITargetingGroup) => g.id === groupId) as ITargetingGroup) || {
      id: groupId,
      items: [],
      operation: TargetingOperation.Or,
    }
  )
}

function getImplicitTargetingFilters({ layout, id }: WidgetSettings): ITargetingFilter[] {
  return layout === WidgetLayout.Inline
    ? [
        {
          type: TargetingFilterType.CssSelector,
          operation: TargetingFilterOperation.Any,
          name: inlineWidgetSelector(id),
          value: null,
        },
      ]
    : []
}

export class Targeting extends Emitter {
  private widgets: { [key: number]: WidgetTargeting } = {}
  public watchers: Watchers = null
  private targetingKeys: TargetingFilterType[] = null

  constructor(private runtime: WidgetRuntime) {
    super()

    this.reset()
  }

  private async getEnvironmentForRuntime(date: Date): Promise<ITargetingEnv> {
    const serverData = await this.runtime.session.getServerData()
    const visit = this.runtime.session.currentVisit
    const utm = visit.utm

    return {
      ab: Math.random() * 100,

      location: visit.location,

      source: visit.source,
      referrer: visit.referrer,
      pageNumber: visit.pageNumber,
      sessionNumber: visit.sessionNumber,
      isReturning: visit.sessionNumber > 1,

      utm: {
        term: utm.utm_term,
        source: utm.utm_source,
        content: utm.utm_content,
        campaign: utm.utm_campaign,
        medium: utm.utm_medium,
      },

      geo: serverData.geo.geopath,

      platform: serverData.ua.platform,
      os: serverData.ua.os,
      browser: serverData.ua.browser,
      language: visit.language,
      displayWidth: visit.displayWidth,
      displayHeight: visit.displayHeight,

      api: this.runtime.args.getParams(),
      ip: serverData.geo.ip,
      cookie: getCookie,
      cssSelector: cssSelect,

      // start
      atonce: true,
      exitAttempted: undefined,
      scrollPercent: undefined,
      timeOnPage: undefined,
      timeOnSite: undefined,
      timeIdle: undefined,

      // schedule
      date: date.getTime(),
      time: date.getTime(),
      weekday: date.getDay(),
    }
  }

  public initialize(widgets: WidgetSettings[]): void {
    this.targetingKeys = getAllTargetingKeys(widgets)

    widgets.forEach(w => {
      this.widgets[w.id] = new WidgetTargeting(w)
    })

    // initialize all watchers
    this.watchers = new Watchers(this.runtime.session.currentVisit, this.targetingKeys)
    this.watchers.on('change', (_, value) => {
      console.log('targeting watcher: ', value)
      this.triggerChange()
    })
  }

  public async getState(now: Date): Promise<TargetingState> {
    let runtimeEnv: ITargetingEnv = {
      ...(await this.getEnvironmentForRuntime(now)),
      ...this.watchers.readValue(),
    }

    // platform environment
    if (this.runtime.platform) {
      const platformKeys = intersection(
        this.targetingKeys,
        this.runtime.platform.getEnvironmentKeys() as TargetingFilterType[],
      )
      if (platformKeys.length) {
        runtimeEnv = {
          ...runtimeEnv,
          ...(await this.runtime.platform.getEnvironment(platformKeys)),
        }
      }
    }

    return new TargetingState(now, runtimeEnv, this.widgets, this.runtime.storages)
  }

  public reset(): void {
    Object.values(this.widgets).forEach(s => s.reset())

    if (this.watchers) {
      this.watchers.reset(this.runtime.session.currentVisit)
    }
  }

  public triggerChange(): void {
    this.trigger('change')
  }
}

export class TargetingState {
  constructor(
    private now: Date,
    private env: ITargetingEnv,
    private widgets: { [key: string]: WidgetTargeting },
    private storages: Storages,
  ) {}

  private getEnvironmentForWidget(widgetId: number, date: Date): Partial<ITargetingEnv> {
    const stat = this.storages.loadAllFor(widgetId) || {}
    const getStat = (event: EventType) => {
      return stat[event] || { time: 0, value: 0 }
    }
    const getSince = (events: EventType[]): number | undefined => {
      const last = events.reduce((m, e) => {
        return Math.max(m, getStat(e).time)
      }, 0)
      return last != 0 ? date.getTime() - last : undefined
    }
    const getCount = (events: EventType[]): number => events.reduce((s, e) => s + getStat(e).value, 0)

    return {
      timeSinceLastAction: getSince(ActionEventTypes),
      actionCount: getCount(ActionEventTypes),

      timeSinceLastClose: getSince([EventType.Close]),
      closeCount: getCount([EventType.Close]),

      timeSinceLastHide: getSince([EventType.Hide]),
      hideCount: getCount([EventType.Hide]),

      timeSinceLastShow: getSince([EventType.Show]),
      showCount: getCount([EventType.Show]),
    }
  }

  public test(widgetId: number, displayedAt?: Date): boolean {
    const widgetTargeting = this.widgets[widgetId]
    if (!widgetTargeting) {
      return false
    }

    const env = {
      ...this.env,
      ...this.getEnvironmentForWidget(widgetId, this.now),
    } as ITargetingEnv
    if (displayedAt) {
      env[TargetingFilterType.TimeDisplay] = this.now.getTime() - displayedAt.getTime()
      const sinceAction = env[TargetingFilterType.TimeSinceLastAction]
      if (env[TargetingFilterType.TimeDisplay] > sinceAction) {
        env[TargetingFilterType.TimeSinceAction] = sinceAction
      }
    }

    return widgetTargeting.test(env, displayedAt != null)
  }
}

export class WidgetTargeting {
  data: ITargetingGroupData

  generalResult: boolean | undefined
  startResult: boolean[] = []
  hideResult: boolean[] = []

  constructor(private widget: WidgetSettings) {
    this.data = (widget.targeting || {}).data

    this.reset()
  }

  public test(env: Partial<ITargetingEnv>, displayed: boolean): boolean {
    const generalResult = targetResults(this.generalTargeting())(env)[0]
    let result = generalResult
    if (!displayed) {
      result = result && targetResults(this.stopTargeting())(env)[0]
    }

    let triggeredType: string
    const [triggers, previousResults] = displayed
      ? [this.hideTriggers(), this.hideResult]
      : [this.startTriggers(), this.startResult]

    triggers.forEach((trigger, ix) => {
      let triggerResult = false
      if (trigger.type === TargetingFilterType.Auto && trigger.value === true) {
        // check is general conditions changed
        triggerResult = displayed
          ? this.generalResult !== false && !generalResult // (true|undefined) -> false for hide
          : this.generalResult !== true && generalResult // (false|undefined) -> true for show
      } else {
        // make targeting
        ;[triggerResult] = targetResults(this.makeTargeting([trigger]))(env)
      }

      if (!triggeredType && triggerResult && !previousResults[ix]) {
        triggeredType = trigger.type
      }

      const isEvent = trigger.operation === TargetingFilterOperation.Is && trigger.value === true
      if (!isEvent) {
        previousResults[ix] = triggerResult
      }
    })

    if (triggeredType) {
      result = result && !displayed
    } else {
      // do nothing - keep closed or displayed
      result = displayed
    }

    this.generalResult = generalResult
    return result
  }

  public reset(): void {
    this.generalResult = undefined
    this.startResult = Array.of(this.startTriggers().length).map(() => false)
    this.hideResult = Array.of(this.hideTriggers().length).map(() => false)
  }

  makeTargeting(items: Array<ITargetingGroup | ITargetingFilter>): ITargeting {
    return {
      group: {
        items: items,
        operation: TargetingOperation.And,
      },
      data: this.data,
    }
  }

  generalTargeting(): ITargeting {
    const groups = [TargetingGroupId.Location, TargetingGroupId.General, TargetingGroupId.Schedule].map(gid =>
      getTargetingGroupOrDefault(this.widget, gid),
    )
    return this.makeTargeting([...groups, ...getImplicitTargetingFilters(this.widget)])
  }

  stopTargeting(): ITargeting {
    return this.makeTargeting([getTargetingGroupOrDefault(this.widget, TargetingGroupId.Stop)])
  }

  startTriggers(): ITargetingFilter[] {
    return getTargetingGroupOrDefault(this.widget, TargetingGroupId.Start).items as ITargetingFilter[]
  }

  hideTriggers(): ITargetingFilter[] {
    return getTargetingGroupOrDefault(this.widget, TargetingGroupId.Hide).items as ITargetingFilter[]
  }
}
